import { useRef } from 'react'
import '@fontsource-variable/fredoka'
import { useI18n } from '@/lib/i18n'
import { PhraseSwap, pick, rand, useCanvasLoop, useCtaText, usePhraseCycle, type SaverProps } from './shared'

const CONFETTI_COLORS = ['#ff4d8d', '#ffd23f', '#3ec5ff', '#7cf29c', '#b26bff', '#ff8a3d']
const LETTER_COLORS = ['#ff4d8d', '#ff8a3d', '#ffc21a', '#2fd47a', '#2fb4ff', '#9b5cff']

interface Bubble { x: number; y: number; r: number; vy: number; wobble: number; phase: number; hue: number; life: number; age: number; pop: number }
interface Confetto { x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string; flip: number; shape: 0 | 1 }

function newBubble(w: number, h: number, anywhere: boolean): Bubble {
  const r = rand(14, 52)
  return { x: rand(0, w), y: anywhere ? rand(0, h) : h + r + rand(0, 80), r, vy: rand(26, 60) * (40 / r) ** 0.3, wobble: rand(8, 26), phase: rand(0, 6.28), hue: rand(0, 360), life: rand(5, 14), age: anywhere ? rand(0, 4) : 0, pop: 0 }
}

function burst(list: Confetto[], x: number, y: number, dir: 1 | -1, h: number) {
  for (let i = 0; i < 70; i++) {
    const speed = rand(0.55, 1.05) * h
    const angle = (-Math.PI / 2) + dir * rand(0.12, 0.62)
    list.push({ x, y, vx: Math.cos(angle) * speed * 0.9, vy: Math.sin(angle) * speed, rot: rand(0, 6.28), vr: rand(-9, 9), size: rand(7, 14), color: pick(CONFETTI_COLORS), flip: rand(0, 6.28), shape: Math.random() < 0.7 ? 0 : 1 })
  }
}

export default function KidsSaver({ phrases, intervalSeconds, wakeMethod }: SaverProps) {
  const { t } = useI18n()
  const cta = useCtaText(wakeMethod)
  const phrase = usePhraseCycle(phrases, Math.max(3, intervalSeconds * 1.6))
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const state = useRef({ bubbles: [] as Bubble[], confetti: [] as Confetto[], nextBurst: 1.2 })

  useCanvasLoop(canvasRef, (ctx, w, h, time, dt) => {
    const s = state.current
    const target = Math.round(Math.min(34, (w * h) / 42000))
    while (s.bubbles.length < target) s.bubbles.push(newBubble(w, h, time < 1))

    for (let i = 0; i < s.bubbles.length; i++) {
      const b = s.bubbles[i]
      if (b.pop > 0) {
        // Pop: a ring that grows and fades, with a few droplets
        b.pop += dt
        const k = b.pop / 0.28
        if (k >= 1) { s.bubbles[i] = newBubble(w, h, false); continue }
        ctx.strokeStyle = `rgba(255,255,255,${(1 - k) * 0.9})`
        ctx.lineWidth = 2.5
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.r * (1 + k * 0.6), 0, Math.PI * 2)
        ctx.stroke()
        ctx.fillStyle = `rgba(255,255,255,${1 - k})`
        for (let d = 0; d < 7; d++) {
          const a = (d / 7) * Math.PI * 2 + b.phase
          ctx.beginPath()
          ctx.arc(b.x + Math.cos(a) * b.r * (1 + k * 1.4), b.y + Math.sin(a) * b.r * (1 + k * 1.4), 2.6 * (1 - k), 0, Math.PI * 2)
          ctx.fill()
        }
        continue
      }
      b.age += dt
      b.y -= b.vy * dt
      const x = b.x + Math.sin(time * 1.3 + b.phase) * b.wobble
      if (b.age > b.life || b.y < -b.r * 2) {
        if (b.y < -b.r * 2) { s.bubbles[i] = newBubble(w, h, false); continue }
        b.pop = 0.0001
        b.x = x
        continue
      }
      const hue = (b.hue + time * 40) % 360
      const body = ctx.createRadialGradient(x - b.r * 0.3, b.y - b.r * 0.35, b.r * 0.1, x, b.y, b.r)
      body.addColorStop(0, 'rgba(255,255,255,0.05)')
      body.addColorStop(0.75, `hsla(${hue}, 90%, 75%, 0.12)`)
      body.addColorStop(0.93, `hsla(${(hue + 60) % 360}, 95%, 70%, 0.45)`)
      body.addColorStop(1, `hsla(${(hue + 140) % 360}, 95%, 80%, 0.65)`)
      ctx.fillStyle = body
      ctx.beginPath()
      ctx.arc(x, b.y, b.r, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = 'rgba(255,255,255,0.85)'
      ctx.beginPath()
      ctx.ellipse(x - b.r * 0.38, b.y - b.r * 0.42, b.r * 0.22, b.r * 0.12, -0.6, 0, Math.PI * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.arc(x + b.r * 0.42, b.y + b.r * 0.36, b.r * 0.06, 0, Math.PI * 2)
      ctx.fill()
    }

    if (time > s.nextBurst) {
      s.nextBurst = time + rand(5.5, 8)
      burst(s.confetti, -10, h + 10, 1, h)
      burst(s.confetti, w + 10, h + 10, -1, h)
    }
    const g = h * 0.9
    s.confetti = s.confetti.filter((c) => c.y < h + 40)
    for (const c of s.confetti) {
      c.vy += g * dt
      c.vx *= 1 - 1.6 * dt
      c.vy *= 1 - 1.2 * dt
      c.x += (c.vx + Math.sin(c.flip) * 30) * dt
      c.y += c.vy * dt
      c.rot += c.vr * dt
      c.flip += dt * 7
      ctx.save()
      ctx.translate(c.x, c.y)
      ctx.rotate(c.rot)
      ctx.scale(1, Math.cos(c.flip))
      ctx.fillStyle = c.color
      if (c.shape === 0) ctx.fillRect(-c.size / 2, -c.size / 4, c.size, c.size / 2)
      else { ctx.beginPath(); ctx.arc(0, 0, c.size / 3, 0, Math.PI * 2); ctx.fill() }
      ctx.restore()
    }
  })

  const hero = t('screensaver.hero.kids')
  const outline = '#fff'
  const textOutline = [
    `0.5vmin 0 0 ${outline}`, `-0.5vmin 0 0 ${outline}`, `0 0.5vmin 0 ${outline}`, `0 -0.5vmin 0 ${outline}`,
    `0.36vmin 0.36vmin 0 ${outline}`, `-0.36vmin 0.36vmin 0 ${outline}`, `0.36vmin -0.36vmin 0 ${outline}`, `-0.36vmin -0.36vmin 0 ${outline}`,
    '0 1.4vmin 0 rgba(60, 20, 120, 0.25)',
  ].join(', ')

  return (
    <div className="ss-kids absolute inset-0 overflow-hidden" style={{ fontFamily: "'Fredoka Variable', 'Geist Variable', sans-serif" }}>
      <style>{KIDS_CSS}</style>
      <div className="ss-kids-sky absolute inset-0" />
      <div className="ss-kids-rays absolute left-1/2 top-[56%] h-[220vmax] w-[220vmax]" />
      {/* Soft clouds */}
      {[['8%', '16%', 1], ['70%', '9%', 1.3], ['82%', '62%', 0.9], ['-4%', '68%', 1.1]].map(([left, top, s], i) => (
        <div key={i} className="ss-kids-cloud absolute" style={{ left: left as string, top: top as string, transform: `scale(${s})`, animationDelay: `${-i * 7}s` }} />
      ))}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-[3vmin] px-[4vw]">
        <h1 className="flex flex-wrap justify-center text-center text-[13vmin] font-bold leading-none" aria-label={hero}>
          {Array.from(hero).map((ch, i) => (
            <span
              key={i}
              className="ss-kids-letter inline-block"
              style={{ color: ch === ' ' ? undefined : LETTER_COLORS[i % LETTER_COLORS.length], animationDelay: `${i * 0.09}s`, textShadow: textOutline, whiteSpace: 'pre' }}
            >
              {ch}
            </span>
          ))}
        </h1>

        <Mascot />

        <div className="relative max-w-[86vw] rounded-[3vmin] bg-white px-[4vmin] py-[1.8vmin] text-center text-[3.6vmin] font-semibold text-[#3b2a7a] shadow-[0_1vmin_0_rgba(59,42,122,0.18)]">
          <div className="absolute -top-[1.6vmin] left-1/2 h-[3vmin] w-[3vmin] -translate-x-1/2 rotate-45 rounded-[0.4vmin] bg-white" />
          <PhraseSwap phrase={phrase} className="relative" />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-[4vmin] flex justify-center">
        <div className="ss-kids-cta rounded-full bg-[#ff4d8d] px-[4.4vmin] py-[1.8vmin] text-[3.2vmin] font-bold text-white shadow-[0_1vmin_0_#c2185b]">
          {cta}
        </div>
      </div>
    </div>
  )
}

function Mascot() {
  return (
    <div className="ss-kids-mascot relative h-[32vmin] w-[42vmin]">
      <div className="ss-kids-burst absolute left-[76%] top-[8%] h-[30vmin] w-[30vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" />
      <svg viewBox="0 0 340 270" className="relative h-full w-full overflow-visible">
        <defs>
          <radialGradient id="kids-glass" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0" stopColor="#8ef3ff" />
            <stop offset="0.45" stopColor="#3b82f6" />
            <stop offset="1" stopColor="#1e1b4b" />
          </radialGradient>
          <linearGradient id="kids-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffd84d" />
            <stop offset="1" stopColor="#ffb21a" />
          </linearGradient>
        </defs>
        <ellipse cx="170" cy="262" rx="120" ry="10" fill="rgba(60,20,120,0.18)" className="ss-kids-shadow" />
        {/* top: viewfinder hump, shutter button, flash */}
        <rect x="112" y="18" width="116" height="50" rx="18" fill="#ff5c9a" />
        <rect x="34" y="30" width="46" height="24" rx="10" fill="#ff3b6b" className="ss-kids-button" />
        <rect x="248" y="30" width="58" height="34" rx="9" fill="#fff6c9" stroke="#ff5c9a" strokeWidth="6" className="ss-kids-flash" />
        {/* body */}
        <rect x="14" y="52" width="312" height="200" rx="48" fill="url(#kids-body)" />
        <rect x="14" y="52" width="312" height="200" rx="48" fill="none" stroke="#ff9f1a" strokeWidth="5" />
        {/* cheeks */}
        <ellipse cx="58" cy="168" rx="20" ry="12" fill="#ff7aa8" opacity="0.7" />
        <ellipse cx="282" cy="168" rx="20" ry="12" fill="#ff7aa8" opacity="0.7" />
        {/* eyes */}
        <g className="ss-kids-eyes">
          <ellipse cx="62" cy="118" rx="22" ry="26" fill="#fff" />
          <ellipse cx="278" cy="118" rx="22" ry="26" fill="#fff" />
          <g className="ss-kids-pupils">
            <circle cx="64" cy="122" r="12" fill="#2a1b55" />
            <circle cx="280" cy="122" r="12" fill="#2a1b55" />
            <circle cx="68" cy="116" r="4" fill="#fff" />
            <circle cx="284" cy="116" r="4" fill="#fff" />
          </g>
        </g>
        {/* lens */}
        <circle cx="170" cy="146" r="80" fill="#3d2f73" />
        <circle cx="170" cy="146" r="68" fill="#5b4aa0" />
        <circle cx="170" cy="146" r="56" fill="url(#kids-glass)" />
        <g className="ss-kids-iris" style={{ transformOrigin: '170px 146px' }}>
          <circle cx="170" cy="146" r="24" fill="#120c33" />
        </g>
        <ellipse cx="150" cy="122" rx="17" ry="10" fill="rgba(255,255,255,0.85)" transform="rotate(-30 150 122)" />
        <circle cx="194" cy="170" r="6" fill="rgba(255,255,255,0.6)" />
        {/* smile */}
        <path d="M 146 236 Q 170 252 194 236" fill="none" stroke="#7a3b00" strokeWidth="6" strokeLinecap="round" />
      </svg>
    </div>
  )
}

const KIDS_CSS = `
.ss-kids-sky { background: linear-gradient(160deg, #5ec8ff, #8b7bff 35%, #ff7ac0 68%, #ffd36b); background-size: 300% 300%; animation: ss-kids-sky 22s ease-in-out infinite alternate; }
@keyframes ss-kids-sky { 0% { background-position: 0% 0% } 100% { background-position: 100% 100% } }
.ss-kids-rays { transform: translate(-50%, -50%); background: repeating-conic-gradient(rgba(255,255,255,0.16) 0 9deg, rgba(255,255,255,0) 9deg 18deg); -webkit-mask-image: radial-gradient(circle, #000 0%, transparent 32%); mask-image: radial-gradient(circle, #000 0%, transparent 32%); animation: ss-kids-spin 80s linear infinite; }
@keyframes ss-kids-spin { to { transform: translate(-50%, -50%) rotate(360deg) } }
.ss-kids-cloud { width: 26vmin; height: 9vmin; background: rgba(255,255,255,0.85); border-radius: 9vmin; filter: blur(0.3vmin); animation: ss-kids-drift 28s ease-in-out infinite alternate; }
.ss-kids-cloud::before, .ss-kids-cloud::after { content: ''; position: absolute; background: inherit; border-radius: 50%; }
.ss-kids-cloud::before { width: 11vmin; height: 11vmin; left: 4vmin; top: -5vmin; }
.ss-kids-cloud::after { width: 8vmin; height: 8vmin; left: 13vmin; top: -3.5vmin; }
@keyframes ss-kids-drift { from { translate: -4vmin 0 } to { translate: 6vmin 1vmin } }
.ss-kids-letter { animation: ss-kids-hop 2.4s cubic-bezier(.3,1.6,.5,1) infinite; transform-origin: 50% 100%; }
@keyframes ss-kids-hop { 0%, 55%, 100% { transform: translateY(0) rotate(0) scale(1) } 18% { transform: translateY(-2.6vmin) rotate(-6deg) scale(1.08, 0.95) } 34% { transform: translateY(0) rotate(3deg) scale(0.96, 1.05) } }
.ss-kids-mascot { animation: ss-kids-bounce 1.6s ease-in-out infinite; transform-origin: 50% 100%; }
@keyframes ss-kids-bounce { 0%, 100% { transform: translateY(0) scale(1.05, 0.94) } 12% { transform: translateY(0) scale(0.96, 1.05) } 50% { transform: translateY(-4vmin) scale(1) } 88% { transform: translateY(0) scale(1) } }
.ss-kids-shadow { transform-origin: 170px 262px; animation: ss-kids-shadow 1.6s ease-in-out infinite; }
@keyframes ss-kids-shadow { 0%, 100% { transform: scale(1); opacity: 1 } 50% { transform: scale(0.75); opacity: 0.5 } }
.ss-kids-eyes { transform-origin: 170px 118px; animation: ss-kids-blink 4.2s infinite; }
@keyframes ss-kids-blink { 0%, 92%, 100% { transform: scaleY(1) } 95% { transform: scaleY(0.08) } }
.ss-kids-pupils { animation: ss-kids-look 7s ease-in-out infinite; }
@keyframes ss-kids-look { 0%, 20% { transform: translate(0, 0) } 28%, 45% { transform: translate(-7px, 2px) } 53%, 70% { transform: translate(7px, -2px) } 78%, 100% { transform: translate(0, 0) } }
.ss-kids-iris { animation: ss-kids-iris 5s ease-in-out infinite; }
@keyframes ss-kids-iris { 0%, 84%, 100% { transform: scale(1) } 88% { transform: scale(0.35) } 93% { transform: scale(1.25) } }
.ss-kids-flash { animation: ss-kids-flash 5s infinite; }
@keyframes ss-kids-flash { 0%, 86%, 100% { fill: #fff6c9 } 88%, 91% { fill: #ffffff; filter: drop-shadow(0 0 18px #fff) } }
.ss-kids-burst { background: radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 25%, transparent 60%); opacity: 0; animation: ss-kids-burst 5s infinite; }
@keyframes ss-kids-burst { 0%, 86%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(0.4) } 88% { opacity: 1; transform: translate(-50%, -50%) scale(1.3) } 95% { opacity: 0; transform: translate(-50%, -50%) scale(1.8) } }
.ss-kids-button { animation: ss-kids-press 5s infinite; transform-origin: 57px 54px; }
@keyframes ss-kids-press { 0%, 82%, 92%, 100% { transform: scaleY(1) } 86% { transform: scaleY(0.5) } }
.ss-kids-cta { animation: ss-kids-pulse 1.6s ease-in-out infinite; }
@keyframes ss-kids-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.06) } }
`
